import { Field } from "@base-ui/react/field";
import { Checkbox as BaseCheckbox } from "@base-ui/react/checkbox";
import { cn } from "@marketing-sites/utils/cn";
import type { ValidationRule } from "@marketing-sites/utils/validation";
import { composeValidate } from "@marketing-sites/utils/validation";
import {
  FieldErrors,
  type FieldErrorsProps,
} from "../form/validation/field-errors";

export type CheckboxProps = Omit<BaseCheckbox.Root.Props, "parent"> & {
  label?: string;
  /** Props forwarded to the Field.Root wrapper. */
  fieldProps?: Omit<Field.Root.Props, "name" | "validate">;
  validations?: ValidationRule[];
  errorProps?: FieldErrorsProps["errorProps"];
  indicator?: React.ReactNode;
  requiredMessage?: string;
};

/**
 * A labelled checkbox with built-in error display.
 *
 * Must be rendered inside a Base UI `<Form>` for validation and error
 * messages to work. Without it, `Field.Error` will never appear because
 * the validation lifecycle (`field.validate()` on submit) is not triggered.
 *
 * @see https://base-ui.com/react/components/checkbox
 * @see https://base-ui.com/react/components/field
 */
export function Checkbox({
  name,
  label,
  disabled,
  required,
  className,
  indicator = <CheckIcon />,
  fieldProps,
  validations,
  errorProps,
  requiredMessage,
  ...rootProps
}: CheckboxProps) {
  return (
    <Field.Root
      name={name}
      disabled={disabled}
      validate={composeValidate(validations, required, requiredMessage)}
      {...fieldProps}
    >
      <div className="flex items-start gap-2">
        <BaseCheckbox.Root
          name={name}
          disabled={disabled}
          required={required}
          aria-required={required || undefined}
          {...rootProps}
          className={cn("checkbox", className)}
        >
          <BaseCheckbox.Indicator className="flex items-center justify-center">
            {indicator}
          </BaseCheckbox.Indicator>
        </BaseCheckbox.Root>
        {label && (
          <Field.Label className="checkbox-label">
            {label} {required && <span>*</span>}
          </Field.Label>
        )}
      </div>
      <FieldErrors errorProps={errorProps} requiredMessage={requiredMessage} />
    </Field.Root>
  );
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 6l3 3 5-5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
